/**
 * Document Detail Page — view, edit metadata, download, reprocess, delete.
 *
 * Two-column layout: DocumentViewer on the left, metadata sidebar on the right.
 * Sidebar toggles between read-only display and inline edit mode.
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import {
  ChevronLeft, Download, RefreshCw, Trash2, Pencil, Loader2,
  Clock, CheckCircle, XCircle, User, Printer, ChevronDown, FileSearch,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StarButton } from '@/components/library/star-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { DocumentViewer } from '@/components/document-viewer';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import { useHasPermission } from '@/lib/use-has-permission';
import { useLibraryFolders } from '@/lib/use-library-folders';
import { DocumentFolderValue } from '@/components/library/document-folder-value';
import { FolderListError } from '@/components/library/folder-picker-states';
// LB6-T10 (decision 40): the document type's picker and label come from THE catalogue.
import { DocumentTypeSelect } from '@/components/library/document-type-picker';
import { documentTypeLabel, useDocumentTypes, type DocumentTypeEntry } from '@/lib/use-document-types';
import {
  classificationFields,
  invalidateClassificationPolicy,
  useClassificationPolicy,
  type ClassificationControlsView,
} from '@/lib/use-classification-policy';
import { useQueryClient } from '@tanstack/react-query';
import {
  ItemDepartmentsField,
  useItemDepartmentsDraft,
  type ItemDepartmentsDraft,
} from '@/components/library/item-departments-field';
// Decision 60 (LB6c-T1): the ONE tag field, and its one door.
import { DocumentTagsField, saveDocumentTags, tagSetChanged } from '@/components/library/document-tags-field';
// LB6d-T1: the ONE read-only display of a document's tags.
import { DocumentTagsValue } from '@/components/library/document-tags-value';
import { libraryDocumentsQueryKey, wikiTagsQueryKey } from '@/lib/use-library-documents';
import { exportControlLabel, sensitivityLabel, type ArticleTagShape } from '@argusai/shared';
import { getApiErrorMessage } from '@/lib/api-error';
import { INCOMPLETE_SCOPE_MESSAGE } from '@/components/users/department-scope-picker';
import { ExportControlSelect, SensitivitySelect } from '@/components/library/classification-selects';

export const Route = createFileRoute('/library/doc/$docId')({
  component: DocumentDetailPage,
});

// ─── Types ──────────────────────────────────────────────────────────────────

interface DocDetail {
  id: string;
  title: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  status: string;
  documentType: string | null;
  description: string | null;
  classification: string | null;
  sensitivity: string;
  exportControl: string;
  processingError: string | null;
  chunkCount: number;
  folderId: string | null;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
  /**
   * The document's tags as stored (GET /knowledge/documents/:id, decision 60).
   * Absent until that read answered: the edit form then sends no tag write.
   */
  tags?: ArticleTagShape[];
}

/**
 * R2 P2 #1 — per-chunk inspector wire format. Mirrors the response
 * shape of GET /api/v1/knowledge/documents/:id/chunks. charStart and
 * charEnd are reserved for the v2 chunker (CLAUDE.md §10.3) and arrive
 * as null today.
 */
interface ChunkRow {
  idx: number;
  page: number | null;
  section: string | null;
  contentPreview: string;
  embeddingVectorPresent: boolean;
  charStart: number | null;
  charEnd: number | null;
  sortKey: number;
  tokenCount: number;
}

interface ChunksResponse {
  documentId: string;
  totalChunks: number;
  page: number;
  pageSize: number;
  chunks: ChunkRow[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

// ─── #709 — Classification marking helpers ────────────────────────────────
//
// These derive the "banner string" rendered at the top of classified
// documents and presented in the download/print confirm dialogs. Exported
// indirectly via the module's test imports so the marking rules stay
// pinned even if the badge labels move.

interface ClassifiableDoc {
  sensitivity: string;
  exportControl: string;
}

export function isDocClassified(doc: ClassifiableDoc): boolean {
  const s = (doc.sensitivity ?? 'unclassified').toLowerCase();
  const e = (doc.exportControl ?? 'none').toLowerCase();
  return s !== 'unclassified' || e !== 'none';
}

export function classificationMarking(doc: ClassifiableDoc): string {
  const s = (doc.sensitivity ?? 'unclassified').toUpperCase();
  const e = (doc.exportControl ?? 'none').toUpperCase();
  const parts: string[] = [s];
  if (e !== 'NONE') parts.push(e);
  return `// ${parts.join(' / ')} //`;
}

function fileTypeLabel(mimeType: string, fileName: string): string {
  const ext = fileName.split('.').pop()?.toUpperCase();
  if (ext) return ext;
  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('word')) return 'DOCX';
  if (mimeType.includes('spreadsheet')) return 'XLSX';
  if (mimeType.includes('presentation')) return 'PPTX';
  return 'FILE';
}

function statusBadge(s: string) {
  switch (s) {
    case 'ready':
      return <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"><CheckCircle className="h-3 w-3 mr-1" />Ready</Badge>;
    case 'processing':
      return <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"><Clock className="h-3 w-3 mr-1 animate-spin" />Processing</Badge>;
    case 'failed':
      return <Badge className="bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
    case 'pending':
      return <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    default:
      return <Badge variant="outline">{s}</Badge>;
  }
}

// ─── LB6-T10 — the document's type, from THE catalogue ─────────────────────

/**
 * The badge colours, by a built-in type's POSITION in the catalogue (its
 * shipped order) — the colours the first seven built-ins always wore. No type
 * name is written here: the label is the catalogue row's (decision 40,
 * L6-13); a custom type, a later built-in and a key the catalogue no longer
 * holds take the outline badge, as any unlisted type always did.
 *
 * POSITION IS A CONTRACT WITH THE SEED (review LB6T10-R5): a release that
 * adds a built-in among the first seven, or reorders them, would shift every
 * colour after it. api db/__tests__/document-types-seed-by-key.test.ts ("THE
 * BADGE WELD") pins which built-in wears which colour from this list and the
 * seed's order, so such a release fails there — a deliberate choice, never a
 * silent shift on screen.
 */
const BUILTIN_TYPE_BADGE_COLORS: readonly string[] = [
  'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20',
  'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20',
  'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
];

/** A document's type as a badge: its catalogue label (the stored key when the catalogue no longer holds it). */
export function DocumentTypeBadge({ documentType, types }: { documentType: string; types: readonly DocumentTypeEntry[] | undefined }) {
  const label = documentTypeLabel(types, documentType);
  const builtinIndex = (types ?? []).filter((t) => t.isBuiltin).findIndex((t) => t.name === documentType);
  const colorClass = builtinIndex >= 0 ? BUILTIN_TYPE_BADGE_COLORS[builtinIndex] : undefined;
  if (colorClass) {
    return <Badge className={colorClass}>{label}</Badge>;
  }
  return <Badge variant="outline">{label}</Badge>;
}

/**
 * The Document Type card: the badge while reading, THE picker while editing
 * (the same control the upload dialog uses). Both read the tenant catalogue.
 */
export function DocumentTypeCard({
  editing,
  documentType,
  value,
  onValueChange,
}: {
  editing: boolean;
  /** The stored key (the form's value while editing). */
  documentType: string;
  value: string;
  onValueChange: (v: string) => void;
}) {
  const typesQ = useDocumentTypes();
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Document Type</CardTitle>
      </CardHeader>
      <CardContent>
        {editing ? (
          <DocumentTypeSelect
            value={value}
            onValueChange={onValueChange}
            errorConsequence="The document keeps its current type."
          />
        ) : (
          <DocumentTypeBadge documentType={documentType} types={typesQ.data} />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Page Component ─────────────────────────────────────────────────────────

// ─── LB6-T7 — classification controls follow the frameworks ───────────────

/**
 * The metadata PATCH body. The classification keys go in ONLY for the
 * controls shown (decision 28 / B7): a hidden control sends no key — not the
 * value the form was opened with — so the api never sees a classification
 * the tenant cannot set (it would answer 400 FRAMEWORK_NOT_ACTIVE).
 */
export function documentMetadataPatch(
  doc: { folderId: string | null },
  edit: {
    title: string;
    documentType: string;
    description: string;
    sensitivity: string;
    exportControl: string;
    folderId: string | null;
  },
  controls: ClassificationControlsView,
): Record<string, unknown> {
  const patchBody: Record<string, unknown> = {
    title: edit.title,
    documentType: edit.documentType,
    description: edit.description.trim() || null,
    ...classificationFields(controls, { sensitivity: edit.sensitivity, exportControl: edit.exportControl }),
  };
  // #140 — Only send folderId when it actually changed so we don't
  // hit the strict() schema for tenants where the backend hasn't
  // yet rolled the new field (defensive — same shape as #694).
  if (edit.folderId !== doc.folderId) {
    patchBody.folderId = edit.folderId;
  }
  return patchBody;
}

/**
 * The Classification card of the detail page: its badges (reading) and its
 * selects (editing), each only for a control the tenant's frameworks show;
 * no card at all when neither is shown. Exported for the per-state test.
 */
export function DocumentClassificationCard({
  controls,
  editing,
  doc,
  sensitivity,
  exportControl,
  onSensitivityChange,
  onExportControlChange,
}: {
  controls: ClassificationControlsView;
  editing: boolean;
  doc: { sensitivity: string; exportControl: string };
  sensitivity: string;
  exportControl: string;
  onSensitivityChange: (v: string) => void;
  onExportControlChange: (v: string) => void;
}) {
  if (!controls.any) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Classification</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {editing ? (
          <>
            {controls.sensitivity ? (
              <SensitivitySelect value={sensitivity} onChange={onSensitivityChange} />
            ) : null}
            {controls.exportControl ? (
              <ExportControlSelect value={exportControl} onChange={onExportControlChange} />
            ) : null}
          </>
        ) : (
          <>
            {controls.sensitivity ? (
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Sensitivity</span>
                <Badge variant="outline" className="capitalize text-xs">
                  {sensitivityLabel(doc.sensitivity)}
                </Badge>
              </div>
            ) : null}
            {controls.exportControl ? (
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Export Control</span>
                <Badge
                  variant={doc.exportControl !== 'none' ? 'destructive' : 'outline'}
                  className="uppercase text-xs"
                >
                  {exportControlLabel(doc.exportControl)}
                </Badge>
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── LB6-T8 — a document's departments, in its edit form ──────────────────

/**
 * "Visible to departments" in the document's EDIT FORM (decision 31 / B10),
 * with the Users rail's picker; drawn only while editing and only for a person
 * who holds share:library (the key PUT /library/acl/documents/:id/departments
 * declares — OQ-L6-2). Exported for the per-state test.
 */
/**
 * The document's Tags (decision 60): the ONE place this page decides which tag
 * surface to draw, and it draws exactly one.
 *   - Reading (LB6d-T1): the reading person's tags, read-only, for every
 *     reader and with no key — the GET /knowledge/documents/:id that answered
 *     them is the gate. No tags, or tags never read → nothing.
 *   - Editing a document whose tags were read (LB6c-T1): the ONE tag field,
 *     which asks write:library.documents itself — the key of PUT
 *     /knowledge/documents/:id/tags and of the Edit Metadata that opens this
 *     form. A form that never read the tags offers no field and writes none.
 * Exported for its test.
 */
export function DocumentTagsCard({
  editing,
  selectedTagIds,
  tags,
  onToggle,
  disabled,
}: {
  editing: boolean;
  selectedTagIds: string[] | null;
  tags: readonly ArticleTagShape[] | undefined;
  onToggle: (tagId: string) => void;
  disabled?: boolean;
}) {
  if (editing && selectedTagIds !== null) return <DocumentTagsField frame="card" selectedTagIds={selectedTagIds} onToggle={onToggle} disabled={disabled} />;
  return <DocumentTagsValue tags={tags ?? []} />;
}

export function DocumentDepartmentsCard({
  editing,
  draft,
  disabled,
}: {
  editing: boolean;
  draft: ItemDepartmentsDraft;
  disabled?: boolean;
}) {
  if (!editing || !draft.allowed) return null;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Visible to departments</CardTitle>
      </CardHeader>
      <CardContent>
        <ItemDepartmentsField draft={draft} disabled={disabled} showLabel={false} />
      </CardContent>
    </Card>
  );
}

function DocumentDetailPage() {
  const { docId } = Route.useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState<DocDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // Library LB6-T2 (L6-17): a failed read is an error, not "Document not
  // found" — only a 404 is. Null while the read succeeded or is a 404.
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  // Edit mode state
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDocType, setEditDocType] = useState('other');
  const [editDescription, setEditDescription] = useState('');
  const [editSensitivity, setEditSensitivity] = useState('unclassified');
  const [editExportControl, setEditExportControl] = useState('none');
  // #140 — folder reassignment in metadata editor.
  const [editFolderId, setEditFolderId] = useState<string | null>(null);
  // Decision 18 — the one folder list (lib/use-library-folders.ts), the same
  // folders the sidebar, upload dialog and Move dialog show.
  const foldersQ = useLibraryFolders();
  const folders = foldersQ.data ?? [];
  // Decision 28 (B7, LB6-T7): the Classification card — its badges and its
  // edit selects — shows only the controls the tenant's compliance
  // frameworks show, and the PATCH carries only those keys. No answer or a
  // failed one hides both and sends neither. The marking banner and the
  // download / print confirmations below are not controls: they mark a
  // document that IS classified, whatever the framework state.
  const { controls: classificationControls } = useClassificationPolicy();
  const queryClient = useQueryClient();
  // Decision 31 (B10, LB6-T8): the document's departments are part of its
  // edit form — read when the form opens, written after the metadata PATCH.
  const departments = useItemDepartmentsDraft({ kind: 'document', id: docId, enabled: editing });
  // Decision 60 (LB6c-T1): the tags ticked in the edit form — null until the
  // form opens on a detail whose tags were read.
  const [editTagIds, setEditTagIds] = useState<string[] | null>(null);

  // Action loading states
  const [reprocessing, setReprocessing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // #709 — a classified download or print is acknowledged first, in an
  // in-app dialog (Library LB6-T2, L6-18: no native confirm in the Library).
  const [pendingClassified, setPendingClassified] = useState<ClassifiedHandling | null>(null);


  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    apiClient.get<DocDetail>(`/knowledge/documents/${docId}`)
      .then(setDoc)
      .catch((err: unknown) => {
        setDoc(null);
        if ((err as { status?: number } | null)?.status !== 404) setLoadError(err);
      })
      .finally(() => setLoading(false));
  }, [docId, loadAttempt]);


  const startEditing = () => {
    if (!doc) return;
    setEditTitle(doc.title);
    setEditDocType(doc.documentType ?? 'other');
    setEditDescription(doc.description ?? '');
    setEditSensitivity(doc.sensitivity ?? 'unclassified');
    setEditExportControl(doc.exportControl ?? 'none');
    setEditFolderId(doc.folderId);
    setEditTagIds(doc.tags ? doc.tags.map((t) => t.id) : null);
    departments.reset();
    setEditing(true);
  };

  const cancelEditing = () => {
    departments.reset();
    setEditTagIds(null);
    setEditing(false);
  };

  const toggleEditTag = (tagId: string) =>
    setEditTagIds((prev) => (prev === null ? prev : prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]));

  const handleSave = async () => {
    if (!doc) return;
    // The api refuses "not all departments, and none picked" (400); say so
    // before anything is written.
    if (!departments.complete) {
      toast.error(INCOMPLETE_SCOPE_MESSAGE);
      return;
    }
    setSaving(true);
    try {
      const patchBody = documentMetadataPatch(
        doc,
        {
          title: editTitle,
          documentType: editDocType,
          description: editDescription,
          sensitivity: editSensitivity,
          exportControl: editExportControl,
          folderId: editFolderId,
        },
        classificationControls,
      );
      const updated = await apiClient.patch<DocDetail>(`/knowledge/documents/${doc.id}`, patchBody);
      // The PATCH answers the metadata; the tags are their own write, below.
      setDoc({ ...updated, tags: doc.tags });
      // Then its departments, if they changed (PUT …/departments). A refusal
      // leaves the form open with the edit in it: the metadata is saved, the
      // departments are what they were.
      try {
        await departments.save(doc.id);
      } catch (depErr) {
        toast.error(`Metadata saved, but the departments were not changed: ${getApiErrorMessage(depErr, 'the request failed')}`);
        setSaving(false);
        return;
      }
      // Then its tags (PUT …/tags), only when the set changed — a detail whose
      // tags were never read sends nothing. A refusal leaves the form open with
      // the edit in it: the metadata and departments are saved, the tags are
      // what they were.
      const storedTagIds = doc.tags ? doc.tags.map((t) => t.id) : null;
      if (editTagIds !== null && tagSetChanged(storedTagIds, editTagIds)) {
        try {
          const tags = await saveDocumentTags(doc.id, editTagIds);
          setDoc((prev) => (prev ? { ...prev, tags } : prev));
          void queryClient.invalidateQueries({ queryKey: wikiTagsQueryKey });
          void queryClient.invalidateQueries({ queryKey: libraryDocumentsQueryKey });
        } catch (tagErr) {
          toast.error(`Metadata saved, but the tags were not changed: ${getApiErrorMessage(tagErr, 'the request failed')}`);
          setSaving(false);
          return;
        }
      }
      setEditTagIds(null);
      setEditing(false);
      toast.success('Metadata updated');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update metadata';
      toast.error(msg);
      // A framework turned on or off since the page loaded answers 400
      // FRAMEWORK_NOT_ACTIVE: re-ask, so the card shows the controls the
      // tenant has now (review LB6T7-BF-06).
      void invalidateClassificationPolicy(queryClient);
    }
    setSaving(false);
  };

  const handleReprocess = async () => {
    if (!doc) return;
    setReprocessing(true);
    try {
      await apiClient.post(`/knowledge/documents/${doc.id}/reprocess`, {});
      toast.success('Reprocessing started');
      // Refresh doc to pick up new status
      const updated = await apiClient.get<DocDetail>(`/knowledge/documents/${doc.id}`);
      setDoc(updated);
    } catch {
      toast.error('Failed to start reprocessing');
    }
    setReprocessing(false);
  };

  const handleDelete = async () => {
    if (!doc) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/knowledge/documents/${doc.id}`);
      toast.success('Document deleted');
      navigate({ to: '/library/documents' });
    } catch {
      toast.error('Failed to delete document');
      setDeleting(false);
    }
  };

  const downloadNow = async () => {
    if (!doc) return;
    try {
      const { downloadFile } = await import('@/lib/upload-utils');
      await downloadFile(`/knowledge/documents/${doc.id}/file`, doc.fileName);
    } catch {
      toast.error('Download failed');
    }
  };

  // #709 — Print path also needs to preserve markings. The
  // classification banner below is rendered with print-visible
  // styling (`print:` Tailwind utilities), so the printed output
  // shows the marking. For classified docs we additionally confirm
  // before invoking window.print() so the user can cancel.
  const handlePrint = () => {
    if (!doc) return;
    if (isDocClassified(doc)) {
      setPendingClassified({ action: 'print', marking: classificationMarking(doc) });
      return;
    }
    window.print();
  };

  // #709 — Classified downloads must acknowledge their marking before the
  // bytes leave the platform context. The backend emits
  // `X-Sensitivity`/`X-Export-Control` headers and prefixes the saved
  // filename; the person is also told up front, in the in-app dialog.
  const handleDownload = async () => {
    if (!doc) return;
    if (isDocClassified(doc)) {
      setPendingClassified({ action: 'download', marking: classificationMarking(doc) });
      return;
    }
    await downloadNow();
  };

  // ── Loading state ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Failed read: an error with a retry, never "not found" (L6-17) ──────
  if (!doc && loadError) {
    return (
      <div className="p-6">
        <Link to="/library/documents">
          <Button variant="ghost"><ChevronLeft className="h-4 w-4 mr-1" />Back to Library</Button>
        </Link>
        <div role="alert" data-testid="document-read-error" className="text-center text-destructive py-16 space-y-2">
          <p>The document could not be loaded: {getApiErrorMessage(loadError, 'the request failed')}</p>
          <Button variant="outline" size="sm" onClick={() => setLoadAttempt((n) => n + 1)}>Retry</Button>
        </div>
      </div>
    );
  }

  // ── Not found state (a 404) ───────────────────────────────────────────────
  if (!doc) {
    return (
      <div className="p-6">
        <Link to="/library/documents">
          <Button variant="ghost"><ChevronLeft className="h-4 w-4 mr-1" />Back to Library</Button>
        </Link>
        <p className="text-center text-destructive py-16">Document not found</p>
      </div>
    );
  }

  const fileUrl = `/api/v1/knowledge/documents/${doc.id}/file`;

  return (
    <div className="p-6 space-y-4">
      {/* #709 — Classification banner. Visible on screen AND in print
          output via the `print:` Tailwind variants. Pre-fix the
          classification was a sidebar badge only; printing left no
          marking on the page at all. */}
      {isDocClassified(doc) && (
        <div
          role="alert"
          data-testid="classification-banner"
          className={
            'rounded-md border-2 px-4 py-2 text-center font-bold tracking-widest text-xs uppercase ' +
            (doc.exportControl !== 'none'
              ? 'border-destructive bg-destructive/10 text-destructive'
              : 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-400') +
            ' print:border-black print:bg-white print:text-black print:!visible print:break-inside-avoid'
          }
        >
          {classificationMarking(doc)}
        </div>
      )}

      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/library/documents">
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Back to documents" title="Back">
              <ChevronLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-1">
              <h1 className="text-xl font-bold">{doc.title}</h1>
              {/* THE personal star, kind 'document' (decision 38). */}
              <StarButton item={{ id: doc.id, kind: 'document' }} size="md" />
            </div>
            <p className="text-sm text-muted-foreground">
              {doc.fileName} &middot; {formatSize(doc.fileSize)}
            </p>
          </div>
        </div>
        <DocumentHeaderActions
          title={doc.title}
          editing={editing}
          onEdit={startEditing}
          onDownload={handleDownload}
          onPrint={handlePrint}
          onReprocess={handleReprocess}
          reprocessing={reprocessing}
          onDelete={handleDelete}
          deleting={deleting}
        />
      </div>

      {/* Two-column layout: viewer + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
        {/* Left: Document Viewer */}
        <DocumentViewer url={fileUrl} fileName={doc.fileName} mimeType={doc.mimeType} height="70vh" />

        {/* Right: Metadata Sidebar */}
        <div className="space-y-4">
          {/* Document Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Document Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Status</span>
                {statusBadge(doc.status)}
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Chunks</span>
                <span className="font-medium">{doc.chunkCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">File Type</span>
                <span className="font-medium">{fileTypeLabel(doc.mimeType, doc.fileName)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Size</span>
                <span className="font-medium">{formatSize(doc.fileSize)}</span>
              </div>
              {doc.processingError && (
                <div className="p-2 bg-red-50 rounded text-xs text-red-700">{doc.processingError}</div>
              )}
            </CardContent>
          </Card>

          {/* Document Type — THE catalogue (LB6-T10, decision 40) */}
          <DocumentTypeCard
            editing={editing}
            documentType={doc.documentType ?? 'other'}
            value={editDocType}
            onValueChange={setEditDocType}
          />

          {/* Title (edit mode only) */}
          {editing && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Title</CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  value={editTitle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditTitle(e.target.value)}
                />
              </CardContent>
            </Card>
          )}

          {/* #140 — Folder. Read-only display when not editing, picker
              when editing so a user can move a previously-uploaded
              document to a different folder without going through
              bulk-move dialogs in the document list. */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Folder</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                // LB2-T3-R3 — a failed read with no list in hand is an error,
                // not a picker offering only the root; the document keeps its
                // folder (editFolderId is unchanged, so the save sends none).
                foldersQ.isError && !foldersQ.data ? (
                  <FolderListError
                    consequence="The document stays in its current folder."
                    onRetry={() => void foldersQ.refetch()}
                  />
                ) : (
                  <Select
                    value={editFolderId ?? '__root__'}
                    onValueChange={(v) => setEditFolderId(v === '__root__' ? null : v)}
                  >
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__root__">No folder (root)</SelectItem>
                      {folders.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              ) : (
                <DocumentFolderValue
                  folderId={doc.folderId}
                  folders={folders}
                  status={foldersQ.status}
                />
              )}
            </CardContent>
          </Card>

          {/* Visible to departments — share:library only (LB6-T8) */}
          <DocumentDepartmentsCard editing={editing} draft={departments} disabled={saving} />

          {/* Tags — the reader's stored tags, read-only (LB6d-T1); while editing, the one tag field (LB6c-T1) */}
          <DocumentTagsCard editing={editing} selectedTagIds={editTagIds} tags={doc.tags} onToggle={toggleEditTag} disabled={saving} />

          {/* Description */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Description</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <Textarea
                  value={editDescription}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditDescription(e.target.value)}
                  placeholder="Brief description of what this document contains"
                  rows={3}
                />
              ) : (
                <p className={`text-sm ${doc.description ? '' : 'text-muted-foreground italic'}`}>
                  {doc.description || 'No description'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Classification — only the controls a framework shows (LB6-T7) */}
          <DocumentClassificationCard
            controls={classificationControls}
            editing={editing}
            doc={doc}
            sensitivity={editSensitivity}
            exportControl={editExportControl}
            onSensitivityChange={setEditSensitivity}
            onExportControlChange={setEditExportControl}
          />

          {/* Metadata (always read-only) */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Metadata</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <User className="h-3 w-3" />Uploaded By
                </span>
                <span className="font-medium truncate max-w-[140px]">{doc.uploadedBy}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />Created
                </span>
                <span className="font-medium">{new Date(doc.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />Updated
                </span>
                <span className="font-medium">{new Date(doc.updatedAt).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>

          {/* Save / Cancel buttons (edit mode) */}
          {editing && (
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={cancelEditing} disabled={saving}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleSave} disabled={saving || !departments.complete}>
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* R2 P2 #1 — Chunk inspector (below; drawn for read:library.documents). */}
      <DocumentChunkInspector docId={doc.id} chunkCount={doc.chunkCount} status={doc.status} />

      <ClassifiedHandlingDialog
        pending={pendingClassified}
        onCancel={() => setPendingClassified(null)}
        onConfirm={(confirmed) => {
          // The dialog has closed — and its overlay is gone — by the time this runs.
          if (confirmed.action === 'download') void downloadNow();
          else window.print();
        }}
      />
    </div>
  );
}

// ─── The classified-handling acknowledgement (#709; LB6-T2, L6-18) ─────────

/** A classified download or print waiting for the person to acknowledge its marking. */
export interface ClassifiedHandling {
  action: 'download' | 'print';
  marking: string;
}

/**
 * "You are about to download / print a classified document" — the in-app
 * dialog that replaced two native `window.confirm`s (L6-18). Exported for
 * the confirm test.
 *
 * The confirmed action runs only AFTER the dialog has gone (LB6T2-REV-01 /
 * BUG-05). Run from the button's click, `window.print()` fired while the
 * dialog and its full-screen overlay were still in the page, so the printout
 * of the classified document carried them. The click now only records what
 * was confirmed; Radix calls `onCloseAutoFocus` once the content has
 * unmounted (after its exit animation), and that hands the action over. A
 * cancel records nothing, so nothing runs.
 */
export function ClassifiedHandlingDialog({
  pending, onCancel, onConfirm,
}: {
  pending: ClassifiedHandling | null;
  onCancel: () => void;
  onConfirm: (pending: ClassifiedHandling) => void;
}) {
  const verb = pending?.action === 'print' ? 'print' : 'download';
  // What the person confirmed, held until the dialog is gone.
  const confirmed = useRef<ClassifiedHandling | null>(null);
  return (
    <AlertDialog open={pending !== null} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <AlertDialogContent
        onCloseAutoFocus={() => {
          const action = confirmed.current;
          confirmed.current = null;
          if (action) onConfirm(action);
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{pending?.marking}</AlertDialogTitle>
          <AlertDialogDescription>
            {verb === 'print'
              ? 'You are about to print a classified document. The printed output will include the classification marking and must be handled per the marking\'s controls.'
              : 'You are about to download a classified document. The saved file will be prefixed with its classification marking and must be handled per the marking\'s controls.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-classified-handling"
            onClick={() => { confirmed.current = pending; }}
          >
            {verb === 'print' ? 'Print' : 'Download'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── The header actions (Library LB6-T3, acceptance L6-04) ─────────────────

/**
 * The detail page's header buttons. Each asks EXACTLY the key its route
 * declares (LIBRARY_CONTROLS in @argusai/shared); one the person may not use
 * is not drawn:
 *   Edit Metadata → write:library.documents (PATCH /knowledge/documents/:id)
 *   Reprocess     → delete:library.documents (POST /knowledge/documents/:id/reprocess)
 *   Delete        → delete:library.documents (DELETE /knowledge/documents/:id)
 * Download and Print read the file the page already shows (read:library).
 * One write key used to gate all three, so a Member saw Delete and Reprocess
 * and was refused with a 403.
 */
export function DocumentHeaderActions({
  title, editing, onEdit, onDownload, onPrint, onReprocess, reprocessing, onDelete, deleting,
}: {
  title: string;
  editing: boolean;
  onEdit: () => void;
  onDownload: () => void;
  onPrint: () => void;
  onReprocess: () => void;
  reprocessing: boolean;
  onDelete: () => void;
  deleting: boolean;
}) {
  const canEditMetadata = useHasPermission('write', 'library.documents');
  const canReprocess = useHasPermission('delete', 'library.documents');
  const canDelete = useHasPermission('delete', 'library.documents');
  return (
    <div className="flex items-center gap-2">
      {canEditMetadata && !editing && (
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Pencil className="h-4 w-4 mr-1" />Edit Metadata
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={onDownload}>
        <Download className="h-4 w-4 mr-1" />Download
      </Button>
      <Button variant="outline" size="sm" onClick={onPrint}>
        <Printer className="h-4 w-4 mr-1" />Print
      </Button>
      {canReprocess && (
        <Button
          variant="outline"
          size="sm"
          onClick={onReprocess}
          disabled={reprocessing}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${reprocessing ? 'animate-spin' : ''}`} />
          Reprocess
        </Button>
      )}
      {canDelete && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive border-destructive hover:bg-destructive/10"
              disabled={deleting}
            >
              <Trash2 className="h-4 w-4 mr-1" />Delete
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Document</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete &ldquo;{title}&rdquo; and all its indexed chunks.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

// ─── The chunk inspector (R2 P2 #1) ────────────────────────────────────────

/**
 * Lazy-loaded Collapsible under the viewer. Surfaces how the document was
 * chunked: order, page, section, embedding-presence flag, and a 200-char
 * content preview per CLAUDE.md §10.4 ("see how a doc was chunked, embedding
 * view"). Closed by default — no network round-trip until the user expands.
 *
 * It reads GET /knowledge/documents/:id/chunks, which declares
 * read:library.documents (L6-02): a person without that key is not shown the
 * section (L6-04) — a Viewer used to open it and get a 403.
 */
export function DocumentChunkInspector({
  docId, chunkCount, status,
}: {
  docId: string;
  chunkCount: number;
  status: string;
}) {
  const canInspectChunks = useHasPermission('read', 'library.documents');
  // Loaded lazily on first open so we don't pay the round-trip for users
  // who never expand the section.
  const [chunksOpen, setChunksOpen] = useState(false);
  const [chunks, setChunks] = useState<ChunkRow[] | null>(null);
  const [chunksLoading, setChunksLoading] = useState(false);
  const [chunksError, setChunksError] = useState<string | null>(null);
  const [chunksTotal, setChunksTotal] = useState<number | null>(null);

  const loadChunks = async () => {
    if (chunks !== null) return; // already loaded
    setChunksLoading(true);
    setChunksError(null);
    try {
      // 50 per page covers ~99.9% of single-doc chunk counts in dev tenants;
      // a future "Load more" affordance can paginate beyond that.
      const data = await apiClient.get<ChunksResponse>(
        `/knowledge/documents/${docId}/chunks?page=1&pageSize=50`,
      );
      setChunks(data.chunks);
      setChunksTotal(data.totalChunks);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load chunks';
      setChunksError(msg);
    } finally {
      setChunksLoading(false);
    }
  };

  const handleChunksOpenChange = (open: boolean) => {
    setChunksOpen(open);
    if (open) {
      void loadChunks();
    }
  };

  if (!canInspectChunks) return null;
  return (
    <Collapsible
      open={chunksOpen}
      onOpenChange={handleChunksOpenChange}
      className="border rounded-lg"
    >
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between px-4 py-3 h-auto font-medium hover:bg-muted/30"
        >
          <span className="flex items-center gap-2">
            <FileSearch className="h-4 w-4 text-muted-foreground" />
            Chunks
            {chunksTotal !== null && (
              <Badge variant="outline" className="text-xs font-mono">
                {chunksTotal}
              </Badge>
            )}
            {chunksTotal === null && chunkCount > 0 && (
              <Badge variant="outline" className="text-xs font-mono text-muted-foreground">
                {chunkCount}
              </Badge>
            )}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${chunksOpen ? 'rotate-180' : ''}`}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t">
        {chunksLoading && (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading chunks...
          </div>
        )}
        {chunksError && !chunksLoading && (
          <div className="p-4 text-sm text-destructive">
            Failed to load chunks: {chunksError}
          </div>
        )}
        {!chunksLoading && !chunksError && chunks !== null && chunks.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">
            No chunks have been indexed for this document yet.
            {status !== 'ready' && (
              <p className="text-xs mt-1">
                Document status is &ldquo;{status}&rdquo;. Chunks appear once ingestion completes.
              </p>
            )}
          </div>
        )}
        {!chunksLoading && !chunksError && chunks !== null && chunks.length > 0 && (
          <>
            {chunksTotal !== null && chunksTotal > chunks.length && (
              <p className="px-4 pt-3 text-xs text-muted-foreground">
                Showing first {chunks.length} of {chunksTotal} chunks. Open the API directly for the full list:
                <code className="ml-1 text-[10px] bg-muted px-1 py-0.5 rounded">
                  GET /api/v1/knowledge/documents/{docId}/chunks
                </code>
              </p>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16 text-xs">#</TableHead>
                    <TableHead className="w-16 text-xs">Page</TableHead>
                    <TableHead className="text-xs">Section</TableHead>
                    <TableHead className="text-xs">Preview (200 chars)</TableHead>
                    <TableHead className="w-20 text-xs text-right">Tokens</TableHead>
                    <TableHead className="w-24 text-xs">Embedding</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chunks.map((c) => (
                    <TableRow key={c.idx}>
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {c.idx}
                      </TableCell>
                      <TableCell className="text-xs font-mono">
                        {c.page ?? '—'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {c.section ?? <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="max-w-md whitespace-pre-wrap break-words text-muted-foreground">
                          {c.contentPreview || (
                            <span className="italic text-muted-foreground/60">
                              (no preview text stored)
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs font-mono text-right text-muted-foreground">
                        {c.tokenCount}
                      </TableCell>
                      <TableCell>
                        {c.embeddingVectorPresent ? (
                          <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px]">
                            <CheckCircle className="h-2.5 w-2.5 mr-1" />
                            Indexed
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-amber-600 dark:text-amber-400 border-amber-500/30">
                            <XCircle className="h-2.5 w-2.5 mr-1" />
                            Missing
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
